import React from 'react'
import {
  Box,
  Chip,
  Select,
  MenuItem,
  Checkbox,
  TextField,
  InputLabel,
  FormControl,
  ListItemText,
  Autocomplete,
  OutlinedInput,
} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'

import { TProcedureFilters } from 'lib/hooks/useFilters'
import { TProcedureFiltersModalProps } from './ProcedureFilters.types'
import { useProcedureFiltersModalLogic } from './useProcedureFiltersModalLogic'
import { CustomModal } from 'components/molecules/Modal/Modal'
import './ProcedureFiltersModal.scss'

const ProcedureFiltersModal: React.FC<TProcedureFiltersModalProps> = ({
  open,
  onClose,
  filters,
  onApplyFilters,
  onClearFilters,
  surgeries = [],
}) => {
  const {
    MenuProps,
    roomOptions,
    localFilters,
    surgeonOptions,
    locationOptions,
    surgeryNameOptions,
    handleApply,
    handleClear,
    setLocalFilters,
    handleArrayFilterChange,
    patientStatusOptions,
  } = useProcedureFiltersModalLogic(
    open,
    filters,
    surgeries,
    onApplyFilters,
    onClearFilters,
    onClose
  )

  return (
    <CustomModal
      open={open}
      header="Filter Procedures"
      primaryButtonText="Apply Filters"
      secondaryButtonText="Clear All"
      secondaryButtonAction={handleClear}
      handleOnSubmit={handleApply}
      icon={<FilterListIcon />}
      onCloseTakeSecondaryButtonAction={false}
      isLoadingState={false}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleApply()
        }}
        className="procedure-filters-modal-form"
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <FormControl fullWidth>
            <InputLabel id="location-filter-label">Sites</InputLabel>
            <Select
              labelId="location-filter-label"
              id="location-filter"
              multiple
              value={localFilters.locations}
              onChange={(e) => handleArrayFilterChange(e, 'locations')}
              input={<OutlinedInput label="Sites" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}
              MenuProps={MenuProps}
            >
              {locationOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox
                    checked={localFilters.locations.indexOf(option.value) > -1}
                  />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="room-filter-label">Locations</InputLabel>
            <Select
              labelId="room-filter-label"
              id="room-filter"
              multiple
              value={localFilters.rooms}
              onChange={(e) => handleArrayFilterChange(e, 'rooms')}
              input={<OutlinedInput label="Locations" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={`Room ${value}`} />
                  ))}
                </Box>
              )}
              MenuProps={MenuProps}
            >
              {roomOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox
                    checked={localFilters.rooms.indexOf(option.value) > -1}
                  />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            id="surgeon-filter"
            options={surgeonOptions}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.label
            }
            value={
              localFilters.surgeonName
                ? {
                    value: localFilters.surgeonName,
                    label: localFilters.surgeonName,
                  }
                : null
            }
            onChange={(_, newValue) => {
              setLocalFilters((prev: TProcedureFilters) => ({
                ...prev,
                surgeonName: newValue ? newValue.value : null,
              }))
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Surgeon Name"
                variant="outlined"
                placeholder="Select a surgeon"
              />
            )}
            isOptionEqualToValue={(option, value) => {
              if (!value) return false
              return typeof value === 'string'
                ? option.value === value
                : option.value === value.value
            }}
          />

          <Autocomplete
            id="procedure-filter"
            options={surgeryNameOptions}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.label
            }
            value={
              localFilters.surgeryName
                ? {
                    value: localFilters.surgeryName,
                    label: localFilters.surgeryName,
                  }
                : null
            }
            onChange={(_, newValue) => {
              setLocalFilters((prev: TProcedureFilters) => ({
                ...prev,
                surgeryName: newValue ? newValue.value : null,
              }))
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Surgery Name"
                variant="outlined"
                placeholder="Select a surgery name"
              />
            )}
            isOptionEqualToValue={(option, value) => {
              if (!value) return false
              return typeof value === 'string'
                ? option.value === value
                : option.value === value.value
            }}
          />

          {patientStatusOptions.length > 0 && (
            <Autocomplete
              id="patient-status-filter"
              options={patientStatusOptions.map((option) => ({
                value: option.id,
                label: option.name,
              }))}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.label
              }
              value={
                localFilters.patientStatus
                  ? {
                      value: localFilters.patientStatus,
                      label:
                        patientStatusOptions.find(
                          (opt) => opt.id === localFilters.patientStatus
                        )?.name || localFilters.patientStatus,
                    }
                  : null
              }
              onChange={(_, newValue) => {
                setLocalFilters((prev: TProcedureFilters) => ({
                  ...prev,
                  patientStatus: newValue ? String(newValue.value) : null,
                }))
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Patient Status"
                  variant="outlined"
                  placeholder="Select a patient status"
                />
              )}
              isOptionEqualToValue={(option, value) => {
                if (!value) return false
                return typeof value === 'string'
                  ? option.value === value
                  : option.value === value.value
              }}
            />
          )}
        </Box>
      </form>
    </CustomModal>
  )
}

export default ProcedureFiltersModal
